import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { format, isToday as isTodayFn } from 'date-fns';
import { url } from '../../../core/utils/link';
import {
    getUserFilterStorageKey,
    ORDER_BY_FILTER_KEY,
    useOverviewFilters,
    useUserFilters,
} from '../../../core/utils/filterQuery';
import { AUTO_UPDATE_INTERVAL } from '../../../../constants';
import { canUseSupportMode } from '../../../users/utils';
import { useCurrentChannel } from '../../../core/utils/hooks/useCurrentChannel.tsx';
import { skipPollAttemptWhenHidden, useRefetchOnTabVisible } from '../../../core/utils/hooks/useRefetchOnTabVisible.ts';
import { capitalizeFirstLetter } from '../../../core/utils/string.ts';
import { PROCESSING_STATUS_COLOR_CLASS } from '../../constants.ts';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries';
import RecordTable, { RecordTableColumn, ROW_CONTROLS_KEY } from '../../../core/components/RecordTable.tsx';
import { RowControlButtonProps } from '../../../core/components/Table.tsx';
import Pagination from '../../../core/components/Pagination';
import DropdownMenu from '../../../core/components/DropdownMenu';
import { withIcon } from '../../../core/components/Icon';
import {
    faArchive,
    faArrowRight,
    faCheckCircle,
    faEllipsisVertical,
    faEnvelope,
    faExternalLink,
    faGears,
    faLock,
    faPhone,
    faPaperclipVertical,
    faRobot,
    faSpinnerThird,
    faTimesCircle,
    faTrash,
    faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import Page from '../../../core/components/Page';
import classnames from '../../../core/utils/classnames.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useSteprunInfo } from '../../../core/utils/steprunConfig';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { camelCase } from 'lodash';
import {
    FINISHED_STATUS_FILTER_CHOICES_WITH_DELETED,
    StatusIndicator,
    TESTING_STATUS_FILTER_CHOICES,
} from './internal/StatusFilter';
import UploadFileModal from './internal/UploadFileModal';
import { AllChannelsHeader, SingleChannelHeader } from './internal/Header';
import FilterBar from './internal/FilterBar';
import { OverviewContextProvider, useOverviewContext } from './internal/OverviewContext';
import AddChannelModal from './internal/AddChannelModal';
import EmptyState from './internal/EmptyState';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext';
import { appInsights } from '../../../core/analytics/applicationInsights';
import { retryDocumentEvent, reUploadDocumentEvent } from '../../../core/analytics/customEvents';
import SelectField from '../../../core/components/Fields/SelectField';
import Button from '../../../core/components/Button';
import DocumentActionModal from '../../components/DocumentActionModal.tsx';
import { formatCustomer } from './internal/ClientFilter.tsx';
import { RowControlIconButton } from '../../../core/components/Table.tsx';
import { MoveToAnotherChannelModal } from './internal/MoveToAnotherChannelModal.tsx';
import { getStatusLabel, isRecordInDiscardableStatus } from '../../utils.ts';
import { mapDocumentTypeToGqlDocumentType } from '../../constants.ts';
import ReprocessDocumentModal from './internal/ReprocessDocumentModal.tsx';
import SmartInboxSvg from '../../../../../static/images/smart_inbox.svg';
import AssignToDropdown, { ChannelUser } from './internal/AssignToDropdown';
import { ASSIGN_TO_DOCUMENTS } from '../Assistance/queries';

const VALUES_MATCH_TRANSLATION_KEY = 'values_match';
const MAX_ITEMS = 500;

const MoreIcon = withIcon(faEllipsisVertical);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);
const LockedIcon = withIcon(faLock);
const MatchIcon = withIcon(faCheckCircle);
const ArrowRightIcon = withIcon(faArrowRight);
const GearsIcon = withIcon(faGears);
const UserGroupIcon = withIcon(faUserGroup);
const NoMatchIcon = withIcon(faTimesCircle);
const ArchiveIcon = withIcon(faArchive);
const DeleteIcon = withIcon(faTrash);
const EmailIcon = withIcon(faEnvelope);
const AttachmentIcon = withIcon(faPaperclipVertical);
const PhoneIcon = withIcon(faPhone);
const RobotIcon = withIcon(faRobot);

const isRecordLockedByUser = (record, user) => record.lockedBy && record.lockedBy?.email !== user?.email;

export const formatSender = (record) => {
    const { t } = useTranslation('assistance');

    const senderEmail = record?.sourcePart?.sender?.email;
    const senderName = record?.sourcePart?.sender?.name;

    if (!senderEmail) {
        return [t('overview.columns.notFound')];
    }

    if (senderName) {
        return [senderName, senderEmail];
    } else {
        return [senderEmail];
    }
};

export const formatTime = (time) => {
    const parsed = new Date(time);
    const isToday = isTodayFn(parsed);

    if (isToday) {
        return format(parsed, 'p'); // Short time format (e.g., "1:30 PM")
    } else {
        return format(parsed, 'PPp'); // Date and time format (e.g., "Apr 29, 2023, 1:30 PM")
    }
};

const MoreButton = ({
    record,
    showDebug,
    onReprocess,
    onRetryStep,
    onDebugStep,
    onDelete,
    onDiscardRecords,
    isDiscardRecordsDisabled,
    onMoveDocumentToAnotherChannel,
    isMoveToAnotherChannelDisabled,
    onAssignUser,
    channelUsers,
    ...props
}: RowControlButtonProps & {
    record: any;
    showDebug: boolean;
    onReprocess: any;
    onRetryStep: any;
    onDebugStep: any;
    onDelete: () => void;

    onDiscardRecords: () => void;
    isDiscardRecordsDisabled: boolean;
    onMoveDocumentToAnotherChannel: () => void;
    isMoveToAnotherChannelDisabled: boolean;
    onAssignUser: (userId: string | null) => Promise<any>;
    channelUsers: ChannelUser[];
}) => {
    const { t } = useTranslation('assistance');
    const { user } = useApplicationContext();

    const { steprunConfig, executionStatus } = useSteprunInfo(record, record?.stepRun);

    const isLocked = isRecordLockedByUser(record, user);
    const isFailed = ['FAILED'].includes(executionStatus);
    const isAssistanceNeeded = record?.stepRun?.manualOnly && record?.stepRun?.executionStatus == 'WAITING_FOR_MANUAL';
    const canRetry = steprunConfig?.canRetry ?? true;
    const canMarkAsCompleted = steprunConfig?.canMarkAsCompleted ?? false;

    const { finished } = useOverviewContext();

    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <RowControlIconButton {...props}>
                        <MoreIcon />
                    </RowControlIconButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content align="end">
                    {!finished && (
                        <>
                            <Tooltip
                                // to hide tooltip on condition we can just pass empty string
                                content={
                                    isMoveToAnotherChannelDisabled
                                        ? t('overview.recordRow.actions.moveDisabledHint')
                                        : ''
                                }
                                side="top"
                                asChild={!isMoveToAnotherChannelDisabled}
                            >
                                <DropdownMenu.Item
                                    disabled={isMoveToAnotherChannelDisabled}
                                    onClick={onMoveDocumentToAnotherChannel}
                                >
                                    {t('overview.recordRow.actions.move')}
                                </DropdownMenu.Item>
                            </Tooltip>

                            <DropdownMenu.Separator />
                        </>
                    )}
                    {!finished && (
                        <>
                            <DropdownMenu.Sub>
                                <DropdownMenu.SubTrigger className="outline-none pl-3">
                                    {t('overview.recordRow.actions.assignTo')}
                                </DropdownMenu.SubTrigger>
                                <DropdownMenu.SubContent className="!max-h-[20rem] !p-0 flex flex-col mr-[8px] -mt-[9px]">
                                    <AssignToDropdown
                                        removeSelected={false}
                                        selectedUser={record.assignedTo?.email}
                                        onSelectedUserChange={onAssignUser}
                                        channelUsers={channelUsers}
                                    />
                                </DropdownMenu.SubContent>
                            </DropdownMenu.Sub>
                            <DropdownMenu.Separator />
                        </>
                    )}
                    {!finished && isAssistanceNeeded && onReprocess && (
                        <DropdownMenu.Item onClick={onReprocess}>
                            {t('overview.recordRow.actions.reprocess')}
                        </DropdownMenu.Item>
                    )}
                    {!finished && isFailed && onRetryStep && canRetry && (
                        <DropdownMenu.Item onClick={onRetryStep}>
                            {t('overview.recordRow.actions.retry')}
                        </DropdownMenu.Item>
                    )}
                    {!finished && isFailed && onRetryStep && canMarkAsCompleted && (
                        // Note: This only works for the pass-through import step right now
                        <DropdownMenu.Item onClick={onRetryStep}>
                            {t('overview.recordRow.actions.markAsCompleted')}
                        </DropdownMenu.Item>
                    )}
                    {onRetryStep && canRetry && showDebug && (
                        <DropdownMenu.Item onClick={onDebugStep}>
                            {t('overview.recordRow.actions.debug')}
                        </DropdownMenu.Item>
                    )}
                    {!finished && (
                        <DropdownMenu.Item disabled={isDiscardRecordsDisabled} onClick={onDiscardRecords}>
                            {t('overview.recordRow.actions.discard')}
                        </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item danger onClick={onDelete} disabled={isLocked}>
                        {t('overview.recordRow.actions.delete')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>
        </>
    );
};

const StatusBadge = ({ status, className, record }: any) => {
    const { t } = useTranslation('assistance');
    const { documentConfiguration } = useOverviewContext();
    const documentTypeName = documentConfiguration?.documentTypeName;
    const statusColorGroup = PROCESSING_STATUS_COLOR_CLASS[status];

    const badge = (
        <span
            className={classnames(
                'inline-flex rounded text-xs font-medium items-baseline px-2 py-1 gap-2 bg-surface-strong text-content-strong border border-solid border-line-strong',
                statusColorGroup == 'success' &&
                    'bg-surface-success-weak text-content-success-strong border-line-success-strong',
                statusColorGroup == 'warning' &&
                    'bg-surface-warning-weak text-content-warning-strong border-line-warning-strong',
                statusColorGroup == 'error' &&
                    'bg-surface-danger-weak text-content-danger-strong border-line-danger-strong',
                className
            )}
        >
            <StatusIndicator status={status} />
            {getStatusLabel(t, status, documentTypeName)}
        </span>
    );

    if (status === 'ON_HOLD' && record?.putOnHoldBy) {
        const user = `${record.putOnHoldBy.firstName} ${record.putOnHoldBy.lastName}`.trim();
        const reason = record.putOnHoldReason || t('header.alerts.onHold.noReason');
        const tooltipContent = (
            <Trans
                i18nKey="header.alerts.onHold.tooltipMessage"
                ns="assistance"
                values={{ user, reason }}
                components={{ bold: <strong /> }}
            />
        );
        return (
            <Tooltip content={tooltipContent} long>
                {badge}
            </Tooltip>
        );
    }

    return badge;
};

export const getArchivedInfo = (record) => {
    if (record.deletedAt) {
        return {
            archivedAt: record.deletedAt,
            archivedBy: record.deletedBy
                ? {
                      email: record.deletedBy.email,
                      name: `${record.deletedBy.firstName} ${record.deletedBy.lastName}`.trim(),
                  }
                : null,
        };
    } else if (record.discardRecord) {
        return {
            archivedAt: record.discardRecord.createdAt,
            archivedBy: record.discardRecord.user
                ? {
                      email: record.discardRecord.user.email,
                      name: `${record.discardRecord.user.firstName} ${record.discardRecord.user.lastName}`.trim(),
                  }
                : null,
        };
    } else if (record.finishedAt) {
        return {
            archivedAt: record.finishedAt,
            archivedBy: record.assistedBy
                ? {
                      email: record.assistedBy.email,
                      name: `${record.assistedBy.firstName} ${record.assistedBy.lastName}`.trim(),
                  }
                : null,
        };
    }
};

const UserCell = ({ user }) => {
    if (!user?.name) {
        return user.email;
    }

    return (
        <span className="inline-flex flex-col gap-1 text-content-strong">
            <span>{user.name}</span>
            <span className="text-xs text-content-weak">{user.email}</span>
        </span>
    );
};

const BatchOperationFloatingMenu = ({
    selectedRowKeys,
    setSelectedRowKeys,
    onMoveToAnotherChannelClicked,
    onBulkReprocessClicked,
    onDiscardRecordsClicked,
    onDeleteRecordsClicked,
    onBulkAssignTo,
    documentConfiguration,
    urlSearchParams,
    tableRefetch,
    channelUsers,
    className,
    ...props
}: React.ComponentPropsWithoutRef<'div'> & {
    selectedRowKeys: string[];
    setSelectedRowKeys: any;
    onMoveToAnotherChannelClicked: () => void;
    onBulkReprocessClicked: () => void;
    onDiscardRecordsClicked: () => void;
    onDeleteRecordsClicked: () => void;
    onBulkAssignTo: (
        userId: string | null,
        recordIds: string[],
        user?: { firstName: string; lastName: string } | null
    ) => void;
    documentConfiguration: any;
    urlSearchParams: any;
    tableRefetch: any;
    channelUsers: ChannelUser[];
}) => {
    const { t } = useTranslation('assistance');

    const handleOpen = () => {
        selectedRowKeys.forEach((recordId) => {
            window.open(
                url(documentConfiguration.constants.ASSISTANCE_PATH, { recordId }, { search: urlSearchParams }),
                '_blank'
            );
        });

        setSelectedRowKeys([]);
    };

    const { finished } = useOverviewContext();

    return (
        <>
            <div
                className={classnames(
                    'sticky bottom-0 w-full flex justify-center left-0 pointer-events-none',
                    className
                )}
                {...props}
            >
                <div className="flex flex-wrap items-center justify-center max-w-full pl-6 pr-3 gap-2 py-2 min-h-14 shadow-xl rounded-xl bg-surface-weak text-content-strong flex-none pointer-events-auto">
                    <div className="text-sm mr-2 flex-none">
                        <strong className="font-medium">{selectedRowKeys.length}</strong>{' '}
                        {t('overview.batchActions.documentsSelected', { count: selectedRowKeys.length })}:
                    </div>

                    <Button variant="ghost" className="flex gap-2 items-center flex-none" onClick={handleOpen}>
                        <span>
                            <ExternalLinkIcon />
                        </span>
                        <span>{t('overview.batchActions.openButton')}</span>
                    </Button>

                    {!finished && (
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Button variant="ghost" className="flex gap-2 items-center flex-none">
                                    <span>
                                        <UserGroupIcon />
                                    </span>
                                    <span>{t('overview.batchActions.assignTo')}</span>
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    align="start"
                                    className="!max-h-[20rem] !p-0 flex flex-col mb-[8px]"
                                >
                                    <AssignToDropdown
                                        removeSelected
                                        selectedUser={null}
                                        onSelectedUserChange={(userId, user) =>
                                            onBulkAssignTo(userId, selectedRowKeys, user)
                                        }
                                        channelUsers={channelUsers}
                                    />
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu>
                    )}

                    {!finished && (
                        <Button
                            variant="ghost"
                            className="flex gap-2 items-center flex-none"
                            onClick={onMoveToAnotherChannelClicked}
                        >
                            <span>
                                <ArrowRightIcon />
                            </span>
                            <span>{t('overview.batchActions.moveToChannel')}</span>
                        </Button>
                    )}

                    {!finished && (
                        <Button
                            variant="ghost"
                            className="flex gap-2 items-center flex-none"
                            onClick={onBulkReprocessClicked}
                        >
                            <span>
                                <GearsIcon />
                            </span>
                            <span>{t('overview.batchActions.bulkReprocess')}</span>
                        </Button>
                    )}

                    <div className="w-px h-6 bg-surface-strong mx-2 flex-none" />

                    {!finished && (
                        <Button
                            variant="ghost"
                            className="flex gap-2 items-center flex-none"
                            onClick={onDiscardRecordsClicked}
                        >
                            <span>
                                <ArchiveIcon />
                            </span>
                            <span>{t('overview.batchActions.discardButton', { count: selectedRowKeys.length })}</span>
                        </Button>
                    )}

                    <Button
                        variant="ghost"
                        className="flex gap-2 items-center hover:bg-surface-danger-weak text-content-danger-strong active:bg-surface-danger-weak active:text-content-danger-strong focus:bg-surface-danger-weak focus:text-content-danger-strong flex-none"
                        onClick={onDeleteRecordsClicked}
                    >
                        <span>
                            <DeleteIcon />
                        </span>
                        <span>{t('overview.batchActions.deleteButton')}</span>
                    </Button>
                </div>
            </div>
        </>
    );
};

const DocumentOverview = ({ documentConfiguration, user, finished, showTestingDocuments }) => {
    const documentName = capitalizeFirstLetter(documentConfiguration.documentTypeName);

    const { t } = useTranslation('assistance');

    const getDeliveryError = (record): string => {
        const { externalReference, externalStatus, externalMessage } = record?.deliveryRecord || {};
        return [
            externalReference && `${t('overview.recordRow.externalReference')}: ${externalReference}`,
            externalStatus && `${t('overview.recordRow.externalStatus')}: ${externalStatus}`,
            externalMessage && `${t('overview.recordRow.externalMessage')}: ${externalMessage}`,
        ]
            .filter((part) => !!part)
            .join('  -  ');
    };

    const navigate = useNavigate();
    const { channelId } = useCurrentChannel();

    const [uploadFileModalVisible, setUploadFileModalVisible] = useState(false);
    const [addChannelVisible, setAddChannelVisible] = useState(false);
    const [duplicateChannelVisible, setDuplicateChannelVisible] = useState(false);
    const [moveToAnotherChannelRecordIds, setMoveToAnotherChannelRecordIds] = useState<string[]>([]);
    const [discardRecordIds, setDiscardRecordIds] = useState<string[]>([]);
    const [discardReason, setDiscardReason] = useState('');
    const [deleteRecordIds, setDeleteRecordIds] = useState<string[]>([]);
    const [reprocessRecordId, setReprocessRecordId] = useState<string | null>(null);
    const [bulkReprocessIds, setBulkReprocessIds] = useState<string[] | null>(null);
    const highlightedRowKeys = useMemo(
        () => moveToAnotherChannelRecordIds.concat(discardRecordIds).concat(deleteRecordIds),
        [moveToAnotherChannelRecordIds, discardRecordIds, deleteRecordIds]
    );

    // By default do not show testing documents in the overview page
    const testing = showTestingDocuments || false;
    const tabKey = testing ? 'testing' : finished ? 'finished' : 'open';
    const [userFilters, setUserFilters, itemsPerPage, setItemsPerPage] = useUserFilters({
        storageKey: getUserFilterStorageKey(documentConfiguration.documentTypeName, channelId, tabKey),
        shouldWriteLocalStorage: true,
        shouldWriteSearchParams: true,
        defaultPageSize: documentConfiguration.constants?.ITEMS_PER_PAGE,
    });
    const overviewFilters = useOverviewFilters({ userFilters, channelId, testing, finished });

    const handleFiltersClear = () => {
        setUserFilters([]);

        // on clear all we also clear all local storage filters
        localStorage.removeItem(getUserFilterStorageKey(documentConfiguration.documentTypeName, channelId, 'open'));
        localStorage.removeItem(getUserFilterStorageKey(documentConfiguration.documentTypeName, channelId, 'testing'));
        localStorage.removeItem(getUserFilterStorageKey(documentConfiguration.documentTypeName, channelId, 'finished'));
    };

    const { data: existingChannelsData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
    });

    const [uploadFile] = useMutation(documentConfiguration.UPLOAD_FILE);
    const [reUploadFile] = useMutation(documentConfiguration.RE_UPLOAD_FILE);
    const [createChannel] = useMutation(documentConfiguration.CREATE_CHANNEL);
    const [retryStep] = useMutation(documentConfiguration.RETRY_STEP);
    const [assignToDocuments] = useMutation(ASSIGN_TO_DOCUMENTS);
    const [isReprocessModalOpen, setIsReprocessModalOpen] = useState(false);

    const handleUploadDone = (isTestingDocument) => {
        if (isTestingDocument) {
            navigate(
                channelId
                    ? url(documentConfiguration.constants.CHANNEL_TESTING_PATH, { channelId })
                    : url(documentConfiguration.constants.OVERVIEW_TESTING_PATH)
            );
        } else {
            navigate(
                channelId
                    ? url(documentConfiguration.constants.CHANNEL_PATH, { channelId })
                    : url(documentConfiguration.constants.OVERVIEW_PATH)
            );
        }
        setUserFilters([]);
        void tableRefetch();
        setUploadFileModalVisible(false);
    };

    const handleUploadCancel = () => {
        setUploadFileModalVisible(false);
    };

    const handleDeleteRecord = () => {
        setSelectedRowKeys((prevRows) => prevRows.filter((rowId) => !deleteRecordIds.includes(rowId)));
        setDeleteRecordIds([]);
    };

    const handleRetryStep = (record) => {
        appInsights?.trackEvent(
            ...retryDocumentEvent(user, record, record?.[documentConfiguration.documentTypeName + 'Unsaved'])
        );
        return retryStep({
            variables: {
                recordId: record.id,
                stepRunId: record.stepRun.id,
            },
            fetchPolicy: 'no-cache',
        }).then(() => {
            void tableRefetch();
        });
    };

    type Assignee = { firstName: string; lastName: string } | null;

    const runAssignMutation = async (recordIds: string[], emailAddress: string | null) => {
        return assignToDocuments({
            variables: {
                documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
                recordIds,
                emailAddress,
            },
            fetchPolicy: 'no-cache',
        });
    };

    const showAssignToast = ({
        emailAddress,
        user,
        count,
        mode, // 'single row' or 'bulk'
    }: {
        emailAddress: string | null;
        user?: Assignee;
        count?: number;
        mode: 'single' | 'bulk';
    }) => {
        const base = mode === 'single' ? 'overview.recordRow.actions' : 'overview.batchActions';
        const descriptionKey = emailAddress === null ? `${base}.assignToUnassignSuccess` : `${base}.assignToSuccess`;

        publishToast({
            description: t(descriptionKey, {
                count,
                firstName: user?.firstName,
                lastName: user?.lastName,
            }),
            status: 'success',
        });
    };

    const showErrorToast = (mode: 'single' | 'bulk') => {
        const base = mode === 'single' ? 'overview.recordRow.actions' : 'overview.batchActions';
        publishToast({
            description: t(`${base}.assignToError`),
            status: 'error',
        });
    };

    const handleAssignUser = (recordId: string) => async (emailAddress: string | null, user?: Assignee) => {
        try {
            await runAssignMutation([recordId], emailAddress);

            showAssignToast({
                emailAddress,
                user,
                mode: 'single',
            });

            void tableRefetch();
        } catch {
            showErrorToast('single');
        }
    };

    const handleBulkAssignTo = async (emailAddress: string | null, recordIds: string[], user?: Assignee) => {
        try {
            await runAssignMutation(recordIds, emailAddress);

            showAssignToast({
                emailAddress,
                user,
                count: recordIds.length,
                mode: 'bulk',
            });

            setSelectedRowKeys([]);
            void tableRefetch();
        } catch {
            showErrorToast('bulk');
        }
    };

    const handleReUploadFile = (record) => {
        appInsights?.trackEvent(
            ...reUploadDocumentEvent(user, record, record?.[documentConfiguration.documentTypeName + 'Unsaved'])
        );
        return reUploadFile({
            variables: {
                files: [],
                recordId: record?.id,
                channelId: record?.channel?.id,
                isTestingDocument: true,
            },
            fetchPolicy: 'no-cache',
        }).then(() => {
            void tableRefetch();
            navigate(
                channelId
                    ? url(documentConfiguration.constants.CHANNEL_TESTING_PATH, { channelId })
                    : url(documentConfiguration.constants.OVERVIEW_TESTING_PATH)
            );
        });
    };

    const handleCreateChannel = ({ name, fromExistingChannelId }) => {
        return createChannel({
            variables: {
                name,
                fromExistingChannelId,
            },
        }).then((res) => {
            const channelId = res?.data?.['create' + documentName + 'ProcessingChannel']?.channel?.id;
            navigate(url(documentConfiguration.constants.CHANNEL_CONFIG_PATH, { channelId }));
        });
    };

    const { publishToast } = useToaster();

    const handleWriteClipboard = (text) => {
        navigator.clipboard
            .writeText(text)
            .then(() =>
                publishToast({ description: t('overview.header.actions.copyEmailAddressSuccess'), status: 'success' })
            )
            .catch(() =>
                publishToast({ description: t('overview.header.actions.copyEmailAddressError'), status: 'error' })
            );
    };

    const [searchParams, setSearchParams] = useSearchParams();

    // We treat active page as 0-based index in code, but in URL it's a 1-based index
    const activePage = parseInt(searchParams.get('page') || '1') - 1;
    function setActivePage(pageNumber: number) {
        setSearchParams((params) => {
            if (pageNumber === 0) {
                params.delete('page');
            } else {
                params.set('page', (pageNumber + 1).toString());
            }
            return params;
        });
    }

    const orderBy = overviewFilters.find((f) => f.name === ORDER_BY_FILTER_KEY)?.value;
    const {
        data,
        previousData,
        loading,
        refetch: tableRefetch,
        client,
    } = useQuery(documentConfiguration.GET_TABLE_OVERVIEW_DATA, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: false,
        pollInterval: AUTO_UPDATE_INTERVAL,
        skipPollAttempt: skipPollAttemptWhenHidden,
        variables: {
            filters: overviewFilters,
            orderBy: Array.isArray(orderBy) ? orderBy[0] : orderBy,
            offset: activePage * itemsPerPage,
            first: itemsPerPage,
            channelId: channelId,
        },
    });
    useRefetchOnTabVisible(tableRefetch);

    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

    useEffect(() => {
        const currentDocCount = data?.['filtered' + documentName + 'ProcessingRecordsList']?.totalCount;
        const previousDocCount = previousData?.['filtered' + documentName + 'ProcessingRecordsList']?.totalCount;
        if (currentDocCount !== undefined && previousDocCount !== undefined && currentDocCount !== previousDocCount) {
            client.refetchQueries({ include: [GET_NAVIGATION_CHANNELS] });
        }
        const newRecordsIds = data?.['filtered' + documentName + 'ProcessingRecordsList']?.records?.map((r) => r.id);
        if (newRecordsIds !== undefined) {
            setSelectedRowKeys((prev) => prev.filter((id) => newRecordsIds.includes(id)));
        }
    }, [data]);

    const { data: channelsData } = useQuery(documentConfiguration.GET_CHANNELS_OVERVIEW_DATA, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
    });
    const channels = channelsData?.[camelCase(documentName) + 'ProcessingChannels'] || [];
    const activeChannel = channels?.find((channel) => channel.id === channelId);

    const channelUsers: ChannelUser[] = useMemo(() => {
        const channelsToFilter = activeChannel ? [activeChannel] : channels;
        const allChannelUsers: ChannelUser[] = channelsToFilter.flatMap((channel) =>
            channel.team?.users ? channel.team.users : []
        );
        const uniqueUsers = Array.from(new Map(allChannelUsers.map((u) => [u.email, u])).values());
        uniqueUsers.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
        uniqueUsers.unshift(user);
        return uniqueUsers;
    }, [activeChannel, channels]);

    const currentData = loading ? previousData : data;
    const totalCount = currentData?.['filtered' + documentName + 'ProcessingRecordsList']?.totalCount;
    const records =
        currentData?.['filtered' + documentName + 'ProcessingRecordsList']?.records?.map((item) => ({
            key: item.id,
            disabled: !!(isRecordLockedByUser(item, user) || item.status === 'PENDING'),
            ...item,
        })) || [];

    const urlSearchParams = {};
    if (channelId) urlSearchParams['channel'] = channelId;
    if (searchParams.get('query')) urlSearchParams['query'] = searchParams.get('query');

    let columns: RecordTableColumn[] = [];

    const calculateSourceChannelIds = (recordIds: string[]): string[] => {
        const channelIds = records.reduce((channelIds, record) => {
            if (recordIds.includes(record.id) && !!record.channel.id) {
                channelIds.add(record.channel.id);
            }

            return channelIds;
        }, new Set<string>());

        return Array.from(channelIds);
    };

    columns.push({
        key: 'document',
        label: t('overview.columns.document'),
        render: (record) => {
            const isLocked = isRecordLockedByUser(record, user);
            const isLoading = ['PENDING', 'SCHEDULED', 'RUNNING', 'ASSISTED'].includes(record.status);

            const title: string = record.title ?? '';
            const subtitle = record.subtitle || undefined;
            const attachmentsCount = record.attachmentsCount ?? 0;
            const iconWithBadgeData: { key: string; icon: React.ReactNode; badgeNumber: number }[] = [];

            if (!!record.emailCount) {
                iconWithBadgeData.push({
                    key: 'email',
                    icon: <EmailIcon className="w-[1.25rem] flex items-center justify-center" />,
                    badgeNumber: record.emailCount,
                });
            }

            if (attachmentsCount > 1) {
                iconWithBadgeData.push({
                    key: 'attachment',
                    icon: <AttachmentIcon className="w-[1.25rem] flex items-center justify-center" />,
                    badgeNumber: attachmentsCount,
                });
            }

            const deliveryError = getDeliveryError(record);

            return (
                <Link
                    className="inline-flex gap-3 items-center w-full max-w-[27.5rem]"
                    to={url(
                        documentConfiguration.constants.ASSISTANCE_PATH,
                        { recordId: record.id },
                        { search: urlSearchParams }
                    )}
                >
                    {isLoading && <LoadingIcon spin className="text-content-brand-strong" />}
                    {!isLoading && isLocked && <LockedIcon className="text-content-medium" />}
                    <div className="flex flex-col text-content-strong overflow-hidden">
                        <span title={title} className="text-sm font-medium text-content-strong line-clamp-2">
                            {title}
                        </span>
                        {subtitle && <span className="mt-0.5 text-sm text-content-strong truncate">{subtitle}</span>}
                        {deliveryError && <span className="mt-2 text-xs">{deliveryError}</span>}
                        {!!iconWithBadgeData.length && (
                            <div className="flex flex-row gap-1 items-center mt-2">
                                {iconWithBadgeData.map((badge) => (
                                    <span key={badge.key} className="flex gap-1 items-center">
                                        {badge.icon}
                                        <span className="font-medium text-xs rounded-full bg-surface-strong px-[0.375rem] min-w-[1.25rem] h-[1.25rem] inline-flex items-center justify-center">
                                            {badge.badgeNumber}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </Link>
            );
        },
        className: 'min-w-96',
    });

    columns.push({
        key: 'status',
        label: t('overview.columns.status'),
        render: (record) => (
            <StatusBadge
                status={(() => {
                    if (record?.deletedAt) {
                        return FINISHED_STATUS_FILTER_CHOICES_WITH_DELETED.DELETED;
                    } else if (testing && record?.isTestDocument && record?.canProcessAutomatically) {
                        // testing documents always go to assistance, however,
                        // those testing docs that would go through automatically should be labelled as such
                        return TESTING_STATUS_FILTER_CHOICES.FINISHED_TESTING;
                    } else {
                        return record.status;
                    }
                })()}
                record={record}
            />
        ),
        className: 'min-w-60 w-60',
    });

    const anyChannelHasMatchingEnabled =
        documentConfiguration?.documentMatchingValidationKey &&
        records.some((record) =>
            record?.channel?.extractorConfig?.validationChecks?.includes(
                documentConfiguration?.documentMatchingValidationKey
            )
        );

    if (anyChannelHasMatchingEnabled) {
        columns.push({
            key: 'matching',
            label: t(`overview.columns.${camelCase(documentConfiguration?.documentMatchingValidationKey)}`),
            render: (record) => {
                const isMatchingEnabled = record?.channel?.extractorConfig?.validationChecks?.includes(
                    documentConfiguration?.documentMatchingValidationKey
                );

                if (!isMatchingEnabled)
                    return (
                        <span className="text-content-medium">{t('overview.recordRow.matchingStatus.notEnabled')}</span>
                    );

                const document = record?.[documentConfiguration.documentTypeName + 'Unsaved'];
                const documentNumberField = document?.[documentConfiguration.documentMatchingFieldName];
                const explanation = documentNumberField?.confidenceExplanation;
                const isMatching =
                    explanation?.explanationDetails?.document_matching_result === VALUES_MATCH_TRANSLATION_KEY;
                const documentNumber = documentNumberField?.value || '';
                let updatedAt = explanation?.explanationDetails?.['time_stamp'] || record?.createdAt || '';
                if (updatedAt) updatedAt = format(new Date(updatedAt), 'PPp');

                const matchingType = camelCase(documentConfiguration?.documentMatchingValidationKey);

                return (
                    <Tooltip
                        content={t(
                            `overview.recordRow.matchingStatus.${matchingType}.${isMatching ? 'match' : 'noMatch'}`,
                            {
                                documentNumber,
                                updatedAt,
                            }
                        )}
                        side="bottom"
                    >
                        <span className="inline-flex gap-2 items-center text-content-strong">
                            {isMatching ? (
                                <MatchIcon className="text-content-success-weak" />
                            ) : (
                                <NoMatchIcon className="text-content-danger-weak" />
                            )}

                            {t(`overview.recordRow.matchingStatusShort.${isMatching ? 'match' : 'noMatch'}`)}
                        </span>
                    </Tooltip>
                );
            },
            className: 'min-w-60 w-60',
        });
    }

    const clientFieldNamesByDocumentType = {
        order: 'client',
        rfq: 'client',
        listOfServices: 'client',
        orderConfirmation: 'supplier',
        invoice: 'supplier',
        deliveryNote: 'supplier',
        propertyBill: 'creditor',
    };

    const clientFieldName = clientFieldNamesByDocumentType[documentConfiguration.documentTypeName] as string;
    if (clientFieldName) {
        columns.push({
            key: clientFieldName,
            label: t(`overview.columns.${clientFieldName}`),
            render: (record) => {
                if (formatCustomer(record.sender) == null) {
                    return <div className="text-content-weak">{t('overview.columns.notFound')}</div>;
                }
                const [name, number] = formatCustomer(record.sender);
                return (
                    <div className="flex flex-col space-y-1">
                        <div>{name}</div>
                        <div className="text-xs text-content-weak">{number || null}</div>
                    </div>
                );
            },
            className: 'min-w-60 w-60',
        });
    }

    if (!channelId) {
        // Add channel column to all channels overview table
        columns.push({
            key: 'channel',
            label: t('overview.columns.channel'),
            render: (record) => record.channel.name,
            className: 'min-w-60 w-60',
        });
    }

    columns.push({
        key: 'assignedTo',
        label: t('overview.columns.assignedTo'),
        render: (record) => {
            return record.assignedTo ? (
                <div className="flex flex-col space-y-1">
                    <div>
                        {record.assignedTo?.firstName || ''} {record.assignedTo?.lastName || ''}
                    </div>
                    {record.assignedTo?.email && (
                        <div className="text-xs text-content-weak">{record.assignedTo?.email}</div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col space-y-1 text-content-medium">
                    <div>({t('overview.recordRow.actions.notAssigned')})</div>
                </div>
            );
        },
        className: 'min-w-60 w-60',
    });

    columns.push({
        key: 'createdBy',
        label: t('overview.columns.createdBy'),
        render: (record) => {
            const isSmartInbox =
                record.createdSource === 'SMART_INBOX' || record.createdSource === 'SMART_INBOX_MANUAL_CREATION';
            const isVoiceRecording = record.createdSource === 'VOICE_RECORDING';

            if (isVoiceRecording) {
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-surface-accent-weak">
                            <PhoneIcon className="w-3.5 h-3.5 text-content-accent-strong" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-content-strong">
                                {t('overview.filters.voiceAgentCreatedBy')}
                            </span>
                        </div>
                    </div>
                );
            }

            return (
                <div className="flex flex-row">
                    {isSmartInbox && (
                        <Tooltip
                            content={t('overview.filters.smartInboxTooltip')}
                            className="w-[170px] text-center"
                            side="top"
                        >
                            <img src={SmartInboxSvg} className="self-center w-[20px] -ml-[28px] h-[20px] mr-2" />
                        </Tooltip>
                    )}

                    {record.createdBy ? (
                        <div className="flex flex-col space-y-1">
                            <div>
                                {record.createdBy.firstName || ''} {record.createdBy.lastName || ''}
                            </div>
                            {record.createdBy.email && (
                                <div className="text-xs text-content-weak">{record.createdBy.email}</div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col space-y-1">
                            <div>{formatSender(record)[0]}</div>
                            <div className="text-xs text-content-weak"> {formatSender(record)[1] || null}</div>
                        </div>
                    )}
                </div>
            );
        },
        className: 'min-w-60 w-60',
    });

    columns.push({
        key: 'createdAt',
        label: t('overview.columns.createdAt'),
        render: (record) => formatTime(record.createdAt),
        className: 'min-w-60 w-60',
    });

    if (documentConfiguration.extraColumns) {
        columns.push(...documentConfiguration.extraColumns);
    }

    if (finished) {
        columns.push({
            key: 'archivedBy',
            label: t('overview.columns.archivedBy'),
            render: (record) => {
                const archivedInfo = getArchivedInfo(record);
                if (!archivedInfo) return '';

                if (!archivedInfo.archivedBy) {
                    return t('overview.archivedByAutomated');
                }

                return <UserCell user={archivedInfo.archivedBy} />;
            },
            className: 'min-w-60 w-60',
        });

        columns.push({
            key: 'archivedAt',
            label: t('overview.columns.archivedAt'),
            render: (record) => (getArchivedInfo(record) ? formatTime(getArchivedInfo(record).archivedAt) : ''),
            className: 'min-w-60 w-60',
        });
    }

    columns.push({
        key: ROW_CONTROLS_KEY,
        label: '',
        render: (record) => {
            return (
                <div className="flex gap-2">
                    <Tooltip content={t('overview.recordRow.openInNewTab')} side="bottom">
                        <RowControlIconButton asChild>
                            <Link
                                to={url(
                                    documentConfiguration.constants.ASSISTANCE_PATH,
                                    { recordId: record.id },
                                    { search: urlSearchParams }
                                )}
                                target="_blank"
                            >
                                <ExternalLinkIcon />
                            </Link>
                        </RowControlIconButton>
                    </Tooltip>
                    <MoreButton
                        record={record}
                        showDebug={canUseSupportMode(user)}
                        onReprocess={() => setReprocessRecordId(record.id)}
                        onRetryStep={() => handleRetryStep(record)}
                        onDebugStep={() => handleReUploadFile(record)}
                        onDelete={() => setDeleteRecordIds([record.id])}
                        isMoveToAnotherChannelDisabled={Object.values(existingChannelsData ?? []).flat().length < 2}
                        onMoveDocumentToAnotherChannel={() => setMoveToAnotherChannelRecordIds([record.id])}
                        isDiscardRecordsDisabled={!isRecordInDiscardableStatus(record.status)}
                        onDiscardRecords={() => {
                            // prefill the reason with the delivery error (empty when there is none)
                            setDiscardReason(getDeliveryError(record));
                            setDiscardRecordIds([record.id]);
                        }}
                        onAssignUser={handleAssignUser(record.id)}
                        channelUsers={channelUsers}
                    />
                </div>
            );
        },
        className: 'min-w-28 w-28',
    });

    return (
        <OverviewContextProvider
            finished={finished}
            testing={testing}
            channelId={channelId}
            documentConfiguration={documentConfiguration}
        >
            <Page>
                <AddChannelModal
                    visible={addChannelVisible}
                    onSubmit={handleCreateChannel}
                    onClose={() => setAddChannelVisible(false)}
                />

                <AddChannelModal
                    visible={duplicateChannelVisible}
                    onSubmit={({ name }) => handleCreateChannel({ name, fromExistingChannelId: channelId })}
                    onClose={() => setDuplicateChannelVisible(false)}
                    title={t('overview.header.actions.duplicateChannel')}
                    data={{
                        channelName: t('overview.header.actions.duplicateChannelNamePrefix') + activeChannel?.name,
                    }}
                />
                {!!moveToAnotherChannelRecordIds.length && (
                    <MoveToAnotherChannelModal
                        documentType={documentConfiguration.documentType}
                        selectedRowKeys={moveToAnotherChannelRecordIds}
                        selectedChannelIds={calculateSourceChannelIds(moveToAnotherChannelRecordIds)}
                        refetch={tableRefetch}
                        onClose={() => setMoveToAnotherChannelRecordIds([])}
                        onSuccess={() => setSelectedRowKeys([])}
                    />
                )}

                {!!discardRecordIds.length && (
                    <DocumentActionModal
                        variant="discard"
                        documentType={documentConfiguration.documentType}
                        selectedRowKeys={discardRecordIds}
                        initialValue={discardReason}
                        refetch={tableRefetch}
                        onClose={() => {
                            setDiscardRecordIds([]);
                            setDiscardReason('');
                        }}
                        onSuccess={() => setSelectedRowKeys([])}
                    />
                )}

                {!!deleteRecordIds.length && (
                    <DocumentActionModal
                        variant="delete"
                        documentType={documentConfiguration.documentType}
                        selectedRowKeys={deleteRecordIds}
                        refetch={tableRefetch}
                        onClose={() => setDeleteRecordIds([])}
                        onSuccess={handleDeleteRecord}
                    />
                )}

                {reprocessRecordId && (
                    <ReprocessDocumentModal
                        recordIds={[reprocessRecordId]}
                        documentType={documentConfiguration.documentType}
                        refetch={tableRefetch}
                        onClose={() => setReprocessRecordId(null)}
                        onSuccess={() => setReprocessRecordId(null)}
                    />
                )}

                {bulkReprocessIds?.length > 0 && (
                    <ReprocessDocumentModal
                        recordIds={bulkReprocessIds}
                        documentType={documentConfiguration.documentType}
                        refetch={tableRefetch}
                        onClose={() => setBulkReprocessIds(null)}
                        onSuccess={() => setBulkReprocessIds(null)}
                    />
                )}

                <UploadFileModal
                    visible={uploadFileModalVisible}
                    activeChannel={activeChannel}
                    uploadFile={uploadFile}
                    onUploadDone={handleUploadDone}
                    channels={channels}
                    onCancel={handleUploadCancel}
                />

                {channelId ? (
                    <SingleChannelHeader
                        activeChannel={activeChannel}
                        onUploadFileModalVisibleChange={setUploadFileModalVisible}
                        onDuplicateChannelVisibleChange={setDuplicateChannelVisible}
                        onCopyEmailAddress={handleWriteClipboard}
                        documentConfiguration={documentConfiguration}
                    />
                ) : (
                    <AllChannelsHeader
                        user={user}
                        onAddChannelVisibleChange={setAddChannelVisible}
                        documentConfiguration={documentConfiguration}
                    />
                )}

                <FilterBar
                    filters={userFilters}
                    onFiltersChange={setUserFilters}
                    onFiltersClear={handleFiltersClear}
                    renderExtraFilters={documentConfiguration.renderExtraFilters}
                    activeChannel={activeChannel}
                    channels={channels}
                    finished={finished}
                />
                <Page.Content lowered className="flex flex-col gap-6 relative">
                    {!loading && totalCount === 0 ? (
                        <EmptyState filters={userFilters} onFiltersChange={setUserFilters} />
                    ) : (
                        <>
                            {!loading && (activePage + 1) * itemsPerPage > MAX_ITEMS ? (
                                <div className="flex flex-col gap-4 items-center justify-center h-full">
                                    <div className="font-medium text-content-strong text-center text-base">
                                        {t('overview.tooManyDocuments.title')}
                                    </div>

                                    <div className="text-content-medium max-w-[32rem] text-center text-base text-pretty">
                                        {t('overview.tooManyDocuments.description', { max_items: MAX_ITEMS })}
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={classnames(
                                        'flex flex-col gap-6 transition-all',
                                        loading && 'animate-pulse opacity-50'
                                    )}
                                >
                                    <RecordTable
                                        columns={columns}
                                        records={records}
                                        loading={loading}
                                        className="max-h-[calc(100dvh-200px)]"
                                        selectable
                                        selectedRowKeys={selectedRowKeys}
                                        highlightedRowKeys={highlightedRowKeys}
                                        onSelectedRowKeysChange={setSelectedRowKeys}
                                    />
                                </div>
                            )}

                            {totalCount > 0 && (
                                <div className="flex flex-wrap items-center gap-3 justify-between">
                                    <span className="text-sm text-content-medium whitespace-nowrap">
                                        {Math.min(totalCount, MAX_ITEMS, activePage * itemsPerPage + 1)} -{' '}
                                        {Math.min(totalCount, MAX_ITEMS, (activePage + 1) * itemsPerPage)} of{' '}
                                        {totalCount}
                                    </span>

                                    <Pagination
                                        numItems={totalCount > MAX_ITEMS ? MAX_ITEMS + itemsPerPage : totalCount}
                                        itemsPerPage={itemsPerPage}
                                        activePage={activePage}
                                        onChange={setActivePage}
                                    />

                                    <SelectField
                                        required
                                        className="w-36 shrink-0 has-data-[state=open]:!bg-surface-brand-medium-default has-data-[state=open]:!border-line-brand-default has-data-[state=open]:text-content-brand-strong font-medium has-data-[state=open]:hover:bg-surface-brand-medium-hover has-data-[state=open]:active:bg-surface-brand-medium-active has-data-[state=open]:focus:bg-surface-brand-medium-focus"
                                        name="pageSize"
                                        value={itemsPerPage.toString()}
                                        options={[
                                            { label: '10', value: '10' },
                                            { label: '20', value: '20' },
                                            { label: '50', value: '50' },
                                            { label: '100', value: '100' },
                                        ]}
                                        renderValue={(value) => t('core:components.pageSizeSelect.option', { value })}
                                        onValueChange={(value: string) => {
                                            setActivePage(0);
                                            setItemsPerPage(Number(value));
                                        }}
                                    />
                                </div>
                            )}

                            {selectedRowKeys.length > 0 && !moveToAnotherChannelRecordIds.length && (
                                <BatchOperationFloatingMenu
                                    selectedRowKeys={selectedRowKeys}
                                    setSelectedRowKeys={setSelectedRowKeys}
                                    documentConfiguration={documentConfiguration}
                                    urlSearchParams={urlSearchParams}
                                    tableRefetch={tableRefetch}
                                    onMoveToAnotherChannelClicked={() =>
                                        setMoveToAnotherChannelRecordIds(selectedRowKeys)
                                    }
                                    onBulkReprocessClicked={() => setBulkReprocessIds(selectedRowKeys)}
                                    onBulkAssignTo={handleBulkAssignTo}
                                    onDiscardRecordsClicked={() => setDiscardRecordIds(selectedRowKeys)}
                                    onDeleteRecordsClicked={() => setDeleteRecordIds(selectedRowKeys)}
                                    channelUsers={channelUsers}
                                />
                            )}
                        </>
                    )}
                </Page.Content>
            </Page>
        </OverviewContextProvider>
    );
};

export default DocumentOverview;
